import React, { useEffect, useState, useRef } from 'react';
import SummaryCard from '../components/SummaryCard';
import { Box, Heading, Text, Flex, Spinner, Alert, AlertIcon, SimpleGrid, Table, Thead, Tbody, Tr, Th, Td, Collapse, IconButton, Button, useToast } from '@chakra-ui/react';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import Page from '../components/ui/Page';
import SectionCard from '../components/ui/SectionCard';
import FilterBar from '../components/ui/FilterBar';
import { useTableDensity } from '../context/TableDensityContext';
import { useSearchParams } from 'react-router-dom';
import { CheckIcon, CloseIcon, ArrowUpIcon, ArrowDownIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import StatusBadge from '../components/ui/StatusBadge';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import AISummaryLoading from '../components/AISummaryLoading';
import client from '../utils/fetchClient';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// replaced with shared StatusBadge

  // Use shared SummaryCard
  

export default function CompareRunsPage({ hideFilters = false, onReady, onRequestExportPdf }) {
  const { density } = useTableDensity();
  const tableSize = density === 'compact' ? 'sm' : 'md';
  const [searchParams, setSearchParams] = useSearchParams();
  let runA = searchParams.get('runA');
  let runB = searchParams.get('runB');

  // State for fetched run metadata
  const [metaA, setMetaA] = useState(null);
  const [metaB, setMetaB] = useState(null);

  // Fetch run metadata to determine order
  useEffect(() => {
    if (runA) {
      client.get(`${API_BASE_URL}/api/test-runs/${runA}`)
        .then(res => res.json())
        .then(json => setMetaA(json.testRun || null))
        .catch(() => setMetaA(null));
    }
    if (runB) {
      client.get(`${API_BASE_URL}/api/test-runs/${runB}`)
        .then(res => res.json())
        .then(json => setMetaB(json.testRun || null))
        .catch(() => setMetaB(null));
    }
  }, [runA, runB]);

  // Swap if needed so runA is earlier
  if (metaA && metaB && metaA.startTime && metaB.startTime) {
    if (new Date(metaA.startTime) > new Date(metaB.startTime)) {
      // Swap
      [runA, runB] = [runB, runA];
    }
  }

  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const [errorA, setErrorA] = useState(null);
  const [errorB, setErrorB] = useState(null);

  // Filtering and sorting state
  const [changeFilter, setChangeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('title');
  const [sortDir, setSortDir] = useState('asc');
  // Initialize optional UI state from URL
  useEffect(() => {
    const qpFilter = searchParams.get('filter');
    const qpSortBy = searchParams.get('sortBy');
    const qpSortDir = searchParams.get('sortDir');
    if (qpFilter) setChangeFilter(qpFilter);
    if (qpSortBy) setSortBy(qpSortBy);
    if (qpSortDir) setSortDir(qpSortDir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync current UI state to URL
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('filter', changeFilter);
    next.set('sortBy', sortBy);
    next.set('sortDir', sortDir);
    setSearchParams(next, { replace: true });
  }, [changeFilter, sortBy, sortDir, searchParams, setSearchParams]);

  // Refs for scrolling to first row of each type
  const rowRefs = {
    fixed: useRef(null),
    'new-failure': useRef(null),
    regression: useRef(null),
    improvement: useRef(null),
    'new-test': useRef(null),
    'removed-test': useRef(null),
  };

  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (title) => setExpandedRows(prev => ({ ...prev, [title]: !prev[title] }));

  const toast = useToast();

  // Add state for AI summary loading and error per row
  const [aiSummaries, setAiSummaries] = useState({});
  const [aiLoading, setAiLoading] = useState({});
  const [aiError, setAiError] = useState({});

  useEffect(() => {
    if (!runA) return;
    setLoadingA(true);
    setErrorA(null);
    client.get(`${API_BASE_URL}/api/test-runs/${runA}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      })
      .then(json => {
        setDataA(json);
        setLoadingA(false);
      })
      .catch(err => {
        setErrorA(err.message || 'Error fetching run A');
        setLoadingA(false);
      });
  }, [runA]);

  useEffect(() => {
    if (!runB) return;
    setLoadingB(true);
    setErrorB(null);
    client.get(`${API_BASE_URL}/api/test-runs/${runB}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      })
      .then(json => {
        setDataB(json);
        setLoadingB(false);
      })
      .catch(err => {
        setErrorB(err.message || 'Error fetching run B');
        setLoadingB(false);
      });
  }, [runB]);

  // Compute summary stats from test case diffs
  let summaryRows = [];
  if (dataA && dataA.testCases && dataB && dataB.testCases) {
    const mapA = new Map((dataA.testCases || []).map(tc => [tc.title, tc]));
    const mapB = new Map((dataB.testCases || []).map(tc => [tc.title, tc]));
    const allTitles = Array.from(new Set([
      ...Array.from(mapA.keys()),
      ...Array.from(mapB.keys())
    ]));
    summaryRows = allTitles.map(title => {
      const tcA = mapA.get(title);
      const tcB = mapB.get(title);
      let diffType = null;
      if (tcA && tcB) {
        if (tcA.status === 'failed' && tcB.status === 'passed') diffType = 'fixed';
        else if (tcA.status === 'passed' && tcB.status === 'failed') diffType = 'new-failure';
        else if (tcA.status === tcB.status && typeof tcA.duration === 'number' && typeof tcB.duration === 'number' && tcB.duration > tcA.duration * 1.25) diffType = 'regression';
        else if (tcA.status === tcB.status && typeof tcA.duration === 'number' && typeof tcB.duration === 'number' && tcB.duration < tcA.duration * 0.75) diffType = 'improvement';
      }
      if (!tcA && tcB) diffType = 'new-test';
      if (tcA && !tcB) diffType = 'removed-test';
      return { title, diffType };
    });
  }

  // Export to CSV
  const handleExportCSV = () => {
    // Build the filtered/sorted rows as in the table
    const mapA = new Map((dataA.testCases || []).map(tc => [tc.title, tc]));
    const mapB = new Map((dataB.testCases || []).map(tc => [tc.title, tc]));
    const allTitles = Array.from(new Set([
      ...Array.from(mapA.keys()),
      ...Array.from(mapB.keys())
    ]));
    const rows = allTitles.map(title => {
      const tcA = mapA.get(title);
      const tcB = mapB.get(title);
      let diffType = null;
      let changeText = '';
      let durationDelta = null;
      if (tcA && tcB) {
        if (tcA.status === 'failed' && tcB.status === 'passed') { diffType = 'fixed'; changeText = 'Fixed'; }
        else if (tcA.status === 'passed' && tcB.status === 'failed') { diffType = 'new-failure'; changeText = 'New Failure'; }
        else if (tcA.status === tcB.status && typeof tcA.duration === 'number' && typeof tcB.duration === 'number' && tcB.duration > tcA.duration * 1.25) { diffType = 'regression'; changeText = 'Performance Regression'; }
        else if (tcA.status === tcB.status && typeof tcA.duration === 'number' && typeof tcB.duration === 'number' && tcB.duration < tcA.duration * 0.75) { diffType = 'improvement'; changeText = 'Performance Improvement'; }
        else if (tcA.status !== tcB.status) { diffType = 'status-change'; changeText = 'Status Changed'; }
        if (typeof tcA.duration === 'number' && typeof tcB.duration === 'number') durationDelta = tcB.duration - tcA.duration;
      }
      if (!tcA && tcB) { diffType = 'new-test'; changeText = 'New Test'; }
      if (tcA && !tcB) { diffType = 'removed-test'; changeText = 'Removed'; }
      return {
        title,
        runAStatus: tcA ? tcA.status : '',
        runBStatus: tcB ? tcB.status : '',
        runADuration: tcA ? tcA.duration : '',
        runBDuration: tcB ? tcB.duration : '',
        durationDelta,
        changeText,
        diffType,
      };
    });
    // Apply filter
    const filtered = changeFilter === 'all' ? rows : rows.filter(r => r.diffType === changeFilter);
    // Apply sort
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'title') return sortDir === 'asc' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
      if (sortBy === 'change') return sortDir === 'asc' ? (a.changeText || '').localeCompare(b.changeText || '') : (b.changeText || '').localeCompare(a.changeText || '');
      if (sortBy === 'duration-delta') {
        const aDelta = a.durationDelta ?? 0;
        const bDelta = b.durationDelta ?? 0;
        return sortDir === 'asc' ? aDelta - bDelta : bDelta - aDelta;
      }
      return 0;
    });
    // Build CSV
    const header = ['Test Case Title', 'Run A Status', 'Run B Status', 'Run A Duration (ms)', 'Run B Duration (ms)', 'Duration Delta', 'Change'];
    const csv = [header.join(',')].concat(sorted.map(r => [
      '"' + r.title.replace(/"/g, '""') + '"',
      r.runAStatus,
      r.runBStatus,
      r.runADuration,
      r.runBDuration,
      r.durationDelta ?? '',
      r.changeText
    ].join(','))).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compare-runs-${runA || 'A'}-vs-${runB || 'B'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: 'Exported to CSV', status: 'success', duration: 2000, isClosable: true });
  };

  // Share link
  const handleShare = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('runA', runA || '');
    url.searchParams.set('runB', runB || '');
    url.searchParams.set('filter', changeFilter);
    url.searchParams.set('sortBy', sortBy);
    url.searchParams.set('sortDir', sortDir);
    navigator.clipboard.writeText(url.toString());
    toast({ title: 'Link copied to clipboard', status: 'info', duration: 2000, isClosable: true });
  };

  // Fetch AI summary for a given row title, only if not already loaded
  const fetchAiSummary = async (row) => {
    const key = row.title;
    if (aiSummaries[key] || aiLoading[key]) return;
    setAiLoading(prev => ({ ...prev, [key]: true }));
    setAiError(prev => ({ ...prev, [key]: null }));
    try {
      console.log('Fetching AI summary for', key); // Debug log
      // Prefer failed error message from Run B, else Run A
      const errorMessage = row.tcB?.errorMessage || row.tcA?.errorMessage || '';
      const errorStack = row.tcB?.errorStack || row.tcA?.errorStack || '';
      const res = await client.post(`${API_BASE_URL}/api/ai-summary`, { body: { errorStack, errorMessage } });
      if (!res.ok) throw new Error('Failed to fetch AI summary');
      const data = await res.json();
      setAiSummaries(prev => ({ ...prev, [key]: data.summary }));
    } catch (err) {
      setAiError(prev => ({ ...prev, [key]: 'Failed to load AI summary.' }));
    } finally {
      setAiLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // useEffect to fetch AI summary when a row is expanded
  useEffect(() => {
    Object.entries(expandedRows).forEach(([title, expanded]) => {
      if (expanded && !aiSummaries[title] && !aiLoading[title] && !aiError[title]) {
        // Find the row data for this title
        const mapA = dataA && dataA.testCases ? new Map((dataA.testCases || []).map(tc => [tc.title, tc])) : new Map();
        const mapB = dataB && dataB.testCases ? new Map((dataB.testCases || []).map(tc => [tc.title, tc])) : new Map();
        const tcA = mapA.get(title);
        const tcB = mapB.get(title);
        if ((tcA && tcA.status === 'failed') || (tcB && tcB.status === 'failed')) {
          fetchAiSummary({ title, tcA, tcB });
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedRows, dataA, dataB]);

  // Notify parent when content is ready (data loaded and present)
  useEffect(() => {
    const ready = !!(dataA && dataA.testCases && dataB && dataB.testCases) && !loadingA && !loadingB;
    if (onReady) onReady(ready);
  }, [dataA, dataB, loadingA, loadingB, onReady]);

  return (
    <Page>
      {/* Header removed; compare filters live on ComparePage. Status filters below remain intact. */}
      {/* Filter and Action Bar (Combined) */}
      {!hideFilters && (
        <FilterBar>
          <Flex gap={2} wrap="wrap" align="center">
            <Text fontWeight="medium">Filter:</Text>
            <Button size="xs" variant={changeFilter === 'all' ? 'solid' : 'outline'} colorScheme="gray" onClick={() => setChangeFilter('all')}>All</Button>
            <Button size="xs" variant={changeFilter === 'fixed' ? 'solid' : 'outline'} colorScheme="green" onClick={() => setChangeFilter('fixed')}>Fixed</Button>
            <Button size="xs" variant={changeFilter === 'new-failure' ? 'solid' : 'outline'} colorScheme="red" onClick={() => setChangeFilter('new-failure')}>New Failure</Button>
            <Button size="xs" variant={changeFilter === 'regression' ? 'solid' : 'outline'} colorScheme="yellow" onClick={() => setChangeFilter('regression')}>Regression</Button>
            <Button size="xs" variant={changeFilter === 'improvement' ? 'solid' : 'outline'} colorScheme="teal" onClick={() => setChangeFilter('improvement')}>Improvement</Button>
            <Button size="xs" variant={changeFilter === 'new-test' ? 'solid' : 'outline'} colorScheme="blue" onClick={() => setChangeFilter('new-test')}>New Test</Button>
            <Button size="xs" variant={changeFilter === 'removed-test' ? 'solid' : 'outline'} colorScheme="gray" onClick={() => setChangeFilter('removed-test')}>Removed</Button>
          </Flex>
          <Flex gap={2} wrap="wrap" align="center">
            <Button colorScheme="blue" size="sm" onClick={handleExportCSV}>Export to CSV</Button>
            <Button colorScheme="blue" size="sm" variant="solid" onClick={() => onRequestExportPdf && onRequestExportPdf()}>Export PDF</Button>
            <Button colorScheme="gray" size="sm" onClick={handleShare}>Share</Button>
          </Flex>
        </FilterBar>
      )}
      {/* Run Panels */}
      <SectionCard mb={6}>
        <Flex gap={4} wrap={{ base: 'wrap', md: 'nowrap' }} align="flex-start">
        {/* Display helpers inlined below */}
        {/* Run A Panel */}
        <Box flex={1} minW={0} borderWidth={1} borderRadius="lg" p={4}>
          <Heading size="md" mb={2}>Run A</Heading>
          {loadingA ? (
            <Flex align="center" justify="center" minH="120px"><Spinner size="lg" /></Flex>
          ) : !runA ? (
            <Text color="red.500">No runA specified</Text>
          ) : errorA ? (
            <Alert status="error" mb={2}><AlertIcon />{errorA}</Alert>
          ) : !dataA || !dataA.testRun ? (
            <Text color="red.500">Run not found</Text>
          ) : (
            <>
              <Flex align="center" gap={2} mb={3}>
                <Text fontWeight="bold" fontSize="lg">{(dataA.testRun.name && dataA.testRun.name.split('/').pop()) || `Run #${dataA.testRun.id}`}</Text>
                <StatusBadge status={dataA.testRun.status} />
              </Flex>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={2}>
                <Box>
                  <Text fontWeight="medium" color="gray.600">Run ID</Text>
                  <Box fontFamily="mono" fontSize="sm" color="gray.700" bg="gray.50" borderRadius="md" p={2} wordBreak="break-all">{dataA.testRun.id}</Box>
                </Box>
                <Box>
                  <Text fontWeight="medium" color="gray.600">User</Text>
                  <Box fontFamily="mono" fontSize="sm" color="gray.700" bg="gray.50" borderRadius="md" p={2} wordBreak="break-all">{dataA.testRun.userId}</Box>
                </Box>
                <Box>
                  <Text fontWeight="medium" color="gray.600">Project</Text>
                  <Box fontFamily="mono" fontSize="sm" color="gray.700" bg="gray.50" borderRadius="md" p={2} wordBreak="break-all">{dataA.testRun.projectId}</Box>
                </Box>
                <Box>
                  <Text fontWeight="medium" color="gray.600">Times</Text>
                  <Flex gap={4} fontSize="sm">
                    <Box><Text color="gray.500">Start</Text><Text>{dataA.testRun.startTime ? new Date(dataA.testRun.startTime).toLocaleString() : '-'}</Text></Box>
                    <Box><Text color="gray.500">End</Text><Text>{dataA.testRun.endTime ? new Date(dataA.testRun.endTime).toLocaleString() : '-'}</Text></Box>
                  </Flex>
                </Box>
              </SimpleGrid>
              <Flex gap={2} wrap="wrap" mb={2}>
                <SimpleGrid columns={2} spacing={2} w="100%">
                  <SummaryCard compact label="Total Tests" value={dataA.testRun.totalTests} color="gray" />
                  <SummaryCard compact label="Passed" value={dataA.testRun.passedTests} color="green" />
                  <SummaryCard compact label="Failed" value={dataA.testRun.failedTests} color="red" />
                  <SummaryCard compact label="Skipped" value={dataA.testRun.skippedTests} color="yellow" />
                </SimpleGrid>
                <Box w="100%">
                  <SummaryCard compact label="Duration (ms)" value={dataA.testRun.totalDuration} color="blue" />
                </Box>
              </Flex>
            </>
          )}
        </Box>
        {/* Run B Panel */}
        <Box flex={1} minW={0} borderWidth={1} borderRadius="lg" p={4}>
          <Heading size="md" mb={2}>Run B</Heading>
          {loadingB ? (
            <Flex align="center" justify="center" minH="120px"><Spinner size="lg" /></Flex>
          ) : !runB ? (
            <Text color="red.500">No runB specified</Text>
          ) : errorB ? (
            <Alert status="error" mb={2}><AlertIcon />{errorB}</Alert>
          ) : !dataB || !dataB.testRun ? (
            <Text color="red.500">Run not found</Text>
          ) : (
            <>
              <Flex align="center" gap={2} mb={3}>
                <Text fontWeight="bold" fontSize="lg">{(dataB.testRun.name && dataB.testRun.name.split('/').pop()) || `Run #${dataB.testRun.id}`}</Text>
                <StatusBadge status={dataB.testRun.status} />
              </Flex>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={2}>
                <Box>
                  <Text fontWeight="medium" color="gray.600">Run ID</Text>
                  <Box fontFamily="mono" fontSize="sm" color="gray.700" bg="gray.50" borderRadius="md" p={2} wordBreak="break-all">{dataB.testRun.id}</Box>
                </Box>
                <Box>
                  <Text fontWeight="medium" color="gray.600">User</Text>
                  <Box fontFamily="mono" fontSize="sm" color="gray.700" bg="gray.50" borderRadius="md" p={2} wordBreak="break-all">{dataB.testRun.userId}</Box>
                </Box>
                <Box>
                  <Text fontWeight="medium" color="gray.600">Project</Text>
                  <Box fontFamily="mono" fontSize="sm" color="gray.700" bg="gray.50" borderRadius="md" p={2} wordBreak="break-all">{dataB.testRun.projectId}</Box>
                </Box>
                <Box>
                  <Text fontWeight="medium" color="gray.600">Times</Text>
                  <Flex gap={4} fontSize="sm">
                    <Box><Text color="gray.500">Start</Text><Text>{dataB.testRun.startTime ? new Date(dataB.testRun.startTime).toLocaleString() : '-'}</Text></Box>
                    <Box><Text color="gray.500">End</Text><Text>{dataB.testRun.endTime ? new Date(dataB.testRun.endTime).toLocaleString() : '-'}</Text></Box>
                  </Flex>
                </Box>
              </SimpleGrid>
              <Flex gap={2} wrap="wrap" mb={2}>
                <SimpleGrid columns={2} spacing={2} w="100%">
                  <SummaryCard compact label="Total Tests" value={dataB.testRun.totalTests} color="gray" />
                  <SummaryCard compact label="Passed" value={dataB.testRun.passedTests} color="green" />
                  <SummaryCard compact label="Failed" value={dataB.testRun.failedTests} color="red" />
                  <SummaryCard compact label="Skipped" value={dataB.testRun.skippedTests} color="yellow" />
                </SimpleGrid>
                <Box w="100%">
                  <SummaryCard compact label="Duration (ms)" value={dataB.testRun.totalDuration} color="blue" />
                </Box>
              </Flex>
            </>
          )}
        </Box>
        </Flex>
      </SectionCard>
      {/* Test Case Diff Section */}
      <SectionCard>
        <Heading size="md" mb={4}>Test Case Comparison</Heading>
      {loadingA || loadingB ? (
        <LoadingState title="Loading comparison" subtitle="Fetching run details and test cases" />
      ) : (!dataA || !dataA.testRun || !dataA.testCases || !dataB || !dataB.testRun || !dataB.testCases) ? (
        <ErrorState message="Test case data not available for both runs." />
      ) : (() => {
        const mapA = new Map((dataA.testCases || []).map(tc => [tc.title, tc]));
        const mapB = new Map((dataB.testCases || []).map(tc => [tc.title, tc]));
        const allTitles = Array.from(new Set([
          ...Array.from(mapA.keys()),
          ...Array.from(mapB.keys())
        ]));
        // Build row data with diffType, icon, etc.
        const rows = allTitles.map(title => {
          const tcA = mapA.get(title);
          const tcB = mapB.get(title);
          let diffType = null;
          let icon = null;
          let rowBg = undefined;
          let changeText = '';
          let changeColor = undefined;
          let durationDelta = null;
          if (tcA && tcB) {
            if (tcA.status === 'failed' && tcB.status === 'passed') {
              diffType = 'fixed';
              icon = <CheckIcon color="green.500" mr={1} />;
              rowBg = 'green.50';
              changeText = 'Fixed';
              changeColor = 'green.700';
            } else if (tcA.status === 'passed' && tcB.status === 'failed') {
              diffType = 'new-failure';
              icon = <CloseIcon color="red.500" mr={1} />;
              rowBg = 'red.50';
              changeText = 'New Failure';
              changeColor = 'red.700';
            } else if (
              tcA.status === tcB.status &&
              typeof tcA.duration === 'number' &&
              typeof tcB.duration === 'number' &&
              tcB.duration > tcA.duration * 1.25
            ) {
              diffType = 'regression';
              icon = <ArrowDownIcon color="red.500" mr={1} />;
              rowBg = 'red.50';
              changeText = 'Performance Regression';
              changeColor = 'red.700';
            } else if (
              tcA.status === tcB.status &&
              typeof tcA.duration === 'number' &&
              typeof tcB.duration === 'number' &&
              tcB.duration < tcA.duration * 0.75
            ) {
              diffType = 'improvement';
              icon = <ArrowUpIcon color="green.600" mr={1} />;
              rowBg = 'green.50';
              changeText = 'Performance Improvement';
              changeColor = 'green.700';
            } else if (tcA.status !== tcB.status) {
              diffType = 'status-change';
              icon = <ArrowUpIcon color="yellow.600" mr={1} />;
              rowBg = 'yellow.50';
              changeText = 'Status Changed';
              changeColor = 'yellow.800';
            }
            if (typeof tcA.duration === 'number' && typeof tcB.duration === 'number') {
              durationDelta = tcB.duration - tcA.duration;
            }
          }
          if (!tcA && tcB) {
            diffType = 'new-test';
            icon = <ArrowUpIcon color="blue.500" mr={1} />;
            rowBg = 'blue.50';
            changeText = 'New Test';
            changeColor = 'blue.700';
          }
          if (tcA && !tcB) {
            diffType = 'removed-test';
            icon = <ArrowDownIcon color="gray.500" mr={1} />;
            rowBg = 'gray.50';
            changeText = 'Removed';
            changeColor = 'gray.700';
          }
          return {
            title,
            tcA,
            tcB,
            diffType,
            icon,
            rowBg,
            changeText,
            changeColor,
            durationDelta,
          };
        });
        // Filter
        const filtered = changeFilter === 'all' ? rows : rows.filter(r => r.diffType === changeFilter);
        // Sort
        const sorted = [...filtered].sort((a, b) => {
          if (sortBy === 'title') {
            return sortDir === 'asc' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
          }
          if (sortBy === 'change') {
            return sortDir === 'asc' ? (a.changeText || '').localeCompare(b.changeText || '') : (b.changeText || '').localeCompare(a.changeText || '');
          }
          if (sortBy === 'duration-delta') {
            const aDelta = a.durationDelta ?? 0;
            const bDelta = b.durationDelta ?? 0;
            return sortDir === 'asc' ? aDelta - bDelta : bDelta - aDelta;
          }
          return 0;
        });
        if (sorted.length === 0) {
          return (
            <Flex direction="column" align="center" justify="center" minH="200px" color="gray.400">
              <Box fontSize="5xl" mb={2}><span role="img" aria-label="empty">🗂️</span></Box>
              <Text fontSize="lg">No test cases match the selected filter.</Text>
            </Flex>
          );
        }
        return (
          <Box>
            <Table display={{ base: 'none', md: 'table' }} variant="striped" size={tableSize} borderWidth={1} borderColor="gray.200" sx={{ th: { position: 'sticky', top: 0, bg: 'white', zIndex: 1 } }}>
              <Thead>
                <Tr>
                  <Th width="32px" p={1} borderWidth={1} borderColor="gray.200"></Th>
                  <Th pl={2} textAlign="left" borderWidth={1} borderColor="gray.200" width="38%">TEST CASE TITLE</Th>
                  <Th borderWidth={1} borderColor="gray.200" width="12%">Run A Status</Th>
                  <Th borderWidth={1} borderColor="gray.200" width="12%">Run B Status</Th>
                  <Th borderWidth={1} borderColor="gray.200"
                    cursor="pointer"
                    onClick={() => {
                      if (sortBy === 'duration-delta') setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                      setSortBy('duration-delta');
                    }}
                  >
                    <Text fontSize="sm">Run A Duration (ms)</Text>
                  </Th>
                  <Th borderWidth={1} borderColor="gray.200"
                    cursor="pointer"
                    onClick={() => {
                      if (sortBy === 'duration-delta') setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                      setSortBy('duration-delta');
                    }}
                  >
                    <Text fontSize="sm">Run B Duration (ms) {sortBy === 'duration-delta' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</Text>
                  </Th>
                  <Th borderWidth={1} borderColor="gray.200"
                    cursor="pointer"
                    onClick={() => {
                      if (sortBy === 'change') setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                      setSortBy('change');
                    }}
                  >
                    <Text fontSize="sm">Change {sortBy === 'change' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</Text>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {sorted.map((row, idx) => [
                  <Tr key={row.title} ref={rowRefs[row.diffType]?.current && idx === summaryRows[row.diffType] ? rowRefs[row.diffType] : null}>
                    <Td width="32px" p={1} textAlign="center" borderWidth={1} borderColor="gray.200">
                      {(row.tcA && row.tcA.status === 'failed') || (row.tcB && row.tcB.status === 'failed') ? (
                        <IconButton
                          icon={expandedRows[row.title] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          size="xs"
                          variant="ghost"
                          aria-label="Toggle error message"
                          onClick={() => toggleRow(row.title)}
                        />
                      ) : null}
                    </Td>
                    <Td pl={2} textAlign="left" fontWeight="medium" borderWidth={1} borderColor="gray.200">
                      {row.title}
                    </Td>
                    <Td borderWidth={1} borderColor="gray.200" fontSize="sm">{row.tcA ? <StatusBadge status={row.tcA.status} /> : <Text color="gray.400">-</Text>}</Td>
                    <Td borderWidth={1} borderColor="gray.200" fontSize="sm">{row.tcB ? <StatusBadge status={row.tcB.status} /> : <Text color="gray.400">-</Text>}</Td>
                    <Td borderWidth={1} borderColor="gray.200" fontSize="sm">{row.tcA ? row.tcA.duration : <Text color="gray.400">-</Text>}</Td>
                    <Td borderWidth={1} borderColor="gray.200" fontSize="sm">{row.tcB ? row.tcB.duration : <Text color="gray.400">-</Text>}</Td>
                    <Td borderWidth={1} borderColor="gray.200">
                      {typeof row.durationDelta === 'number' ? (
                        <Flex align="center" gap={1}>
                          <Box w="40px" h="6px" borderRadius="md" bg={row.durationDelta > 0 ? 'red.200' : row.durationDelta < 0 ? 'green.200' : 'gray.200'}>
                            <Box w={`${Math.min(Math.abs(row.durationDelta) / 1000 * 100, 100)}%`} h="100%" bg={row.durationDelta > 0 ? 'red.500' : row.durationDelta < 0 ? 'green.500' : 'gray.400'} borderRadius="md" />
                          </Box>
                          <Text fontSize="xs" color={row.durationDelta > 0 ? 'red.600' : row.durationDelta < 0 ? 'green.700' : 'gray.600'}>{row.durationDelta > 0 ? '+' : ''}{row.durationDelta}</Text>
                        </Flex>
                      ) : <Text color="gray.400">-</Text>}
                    </Td>
                    <Td borderWidth={1} borderColor="gray.200" color={row.changeColor} fontSize="sm">{row.icon}{row.changeText}</Td>
                  </Tr>,
                  ((row.tcA && row.tcA.status === 'failed') || (row.tcB && row.tcB.status === 'failed')) && expandedRows[row.title] && (
                    <Tr key={row.title + '-err'} bg="gray.50">
                      <Td colSpan={8} p={0} borderWidth={1} borderColor="gray.200">
                        <Collapse in={expandedRows[row.title]} animateOpacity>
                          <Flex gap={8} p={4} align="flex-start">
                            <Box flex={1}>
                              <Text fontWeight="bold" mb={1}>Run A Error</Text>
                              <Box fontSize="sm" color="red.700" bg="red.50" borderRadius="md" p={2} minH="32px">
                                {row.tcA && row.tcA.errorMessage ? row.tcA.errorMessage : <Text color="gray.400">No error</Text>}
                              </Box>
                            </Box>
                            <Box flex={1}>
                              <Text fontWeight="bold" mb={1}>Run B Error</Text>
                              <Box fontSize="sm" color="red.700" bg="red.50" borderRadius="md" p={2} minH="32px">
                                {row.tcB && row.tcB.errorMessage ? row.tcB.errorMessage : <Text color="gray.400">No error</Text>}
                              </Box>
                            </Box>
                            {/* AI Failure Summary */}
                            <Box flex={1} minW="220px">
                              <Text fontWeight="bold" mb={1} color="blue.700">AI Failure Summary</Text>
                              {(() => {
                                if (expandedRows[row.title] && ((row.tcA && row.tcA.status === 'failed') || (row.tcB && row.tcB.status === 'failed'))) {
                                  if (!aiSummaries[row.title] && !aiLoading[row.title] && !aiError[row.title]) {
                                    fetchAiSummary(row);
                                  }
                                  if (aiLoading[row.title]) {
                                    return <AISummaryLoading message="Generating AI summary..." />;
                                  }
                                  if (aiError[row.title]) {
                                    return <Text color="red.500">{aiError[row.title]}</Text>;
                                  }
                                  if (aiSummaries[row.title]) {
                                    return <Box bg="blue.50" color="blue.900" borderRadius="md" p={2} fontSize="sm" boxShadow="sm" border="1px solid #90cdf4">
                                      <ReactMarkdown children={aiSummaries[row.title]} remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]} />
                                    </Box>;
                                  }
                                }
                                return null;
                              })()}
                            </Box>
                          </Flex>
                        </Collapse>
                      </Td>
                    </Tr>
                  )
                ])}
              </Tbody>
            </Table>
            {/* Mobile stacked cards */}
            <Box display={{ base: 'block', md: 'none' }}>
              {sorted.map((row) => (
                <Box key={row.title} borderWidth={1} borderColor="gray.200" borderRadius="lg" p={3} mb={3} bg="white">
                  <Text fontWeight="bold" mb={1}>{row.title}</Text>
                  <Flex justify="space-between" fontSize="sm" mb={1}><Text color="gray.500">Run A Status</Text><Box><StatusBadge status={row.tcA?.status} /></Box></Flex>
                  <Flex justify="space-between" fontSize="sm" mb={1}><Text color="gray.500">Run B Status</Text><Box><StatusBadge status={row.tcB?.status} /></Box></Flex>
                  <Flex justify="space-between" fontSize="sm" mb={1}><Text color="gray.500">Run A Duration</Text><Text>{row.tcA?.duration ?? '-'}</Text></Flex>
                  <Flex justify="space-between" fontSize="sm" mb={2}><Text color="gray.500">Run B Duration</Text><Text>{row.tcB?.duration ?? '-'}</Text></Flex>
                  <Flex justify="space-between" fontSize="sm" mb={2}><Text color="gray.500">Change</Text><Text color={row.changeColor}>{row.changeText || '-'}</Text></Flex>
                  {((row.tcA && row.tcA.status === 'failed') || (row.tcB && row.tcB.status === 'failed')) && (
                    <Box mt={2}>
                      <Text fontWeight="semibold" fontSize="sm" mb={1} color="red.600">Errors</Text>
                      <Box bg="red.50" borderRadius="md" p={2} mb={2}>
                        <Text fontWeight="medium" color="red.700" mb={1}>Run A</Text>
                        <Text fontSize="sm" color="red.700">{row.tcA?.errorMessage || '-'}</Text>
                      </Box>
                      <Box bg="red.50" borderRadius="md" p={2}>
                        <Text fontWeight="medium" color="red.700" mb={1}>Run B</Text>
                        <Text fontSize="sm" color="red.700">{row.tcB?.errorMessage || '-'}</Text>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        );
      })()}
      </SectionCard>
    </Page>
  );
} 